import React from "react";
import { ChevronRight } from "lucide-react";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import GoogleMaps from "google-map-react";
import MaskedInput from "react-text-mask";
import Marker from "../../../components/Marker";
import type { Errors } from "../../../utils/getValidationError";
import { ButtonsWrapper, NextButton, RegisterButton, ResetButton } from "../../CreateProperty/twStyles";
import SaveDraftButton from "../../../components/SaveDraftButton";
import PropertyChildrenSection from "../../../components/PropertyChildrenSection";
import { formSelectStyles } from "../../CreateProperty/components/DescriptionForm/twStyles";
import type { EditPropertyStepId } from "./stepConstants";
import { agentDebugLog } from "../../../utils/agentDebugLog";

export interface EditMapStepProps {
  googleMapsSearch: string;
  setGoogleMapsSearch: (v: string) => void;
  hadleGetMoreInformationsAboutLocal: (
    address: string,
    place_id: string
  ) => Promise<void>;
  googleID: string;
  setGoogleID: (v: string) => void;
  formErrors: Errors;
  logradouro: string;
  setLogradouro: (v: string) => void;
  cep: string;
  setCEP: (v: string) => void;
  number: string;
  setNumber: (v: string) => void;
  complement: string;
  setComplement: (v: string) => void;
  neighborhood: string;
  setNeighborhood: (v: string) => void;
  city: string;
  setCity: (v: string) => void;
  uf: string;
  setUF: (v: string) => void;
  floor: string;
  setFloor: (v: string) => void;
  latitude: number;
  longitude: number;
  setLoadingGoogleMapsApi: (v: boolean) => void;
  loadingRequest: boolean;
  handleGoToDescriptionForm: () => void | Promise<void>;
  setFormOptionSelected: (id: EditPropertyStepId) => void;
  isDraft?: boolean;
  isSavingDraft?: boolean;
  handleClearAddressFields?: () => void;
  handleSaveAddressDraft?: () => void | Promise<void>;
  handleDraftNextFromMap?: () => void;
  propertyId: number;
  rentReasons?: { id: number; reason: string }[];
  hasChildrenUnits: boolean;
  onChildrenCountChange: (count: number) => void;
}

const fieldClass =
  "flex w-full max-w-[920px] flex-col items-start justify-start gap-2 md:mx-[6px] md:flex-1 [&>label]:mb-1 [&>label]:flex [&>label]:items-end [&>label]:text-sm [&>label]:font-medium [&>label]:text-slate-800 [&>label>p]:ml-1.5 [&>label>p]:text-[10px] [&>label>p]:font-bold [&>input]:w-full [&>input]:appearance-none [&>input]:rounded-md [&>input]:border [&>input]:border-solid [&>input]:border-slate-200 [&>input]:bg-white [&>input]:px-3 [&>input]:py-2 [&>input]:text-sm [&>input]:text-slate-800 [&>input]:shadow-none [&>input]:outline-none [&>input]:transition [&>input:focus]:border-slate-400 [&>input:focus]:shadow-none [&>input:focus]:ring-0 [&_input]:w-full [&_input]:appearance-none [&_input]:rounded-md [&_input]:border [&_input]:border-solid [&_input]:border-slate-200 [&_input]:bg-white [&_input]:px-3 [&_input]:py-2 [&_input]:text-sm [&_input]:text-slate-800 [&_input]:shadow-none [&_input]:outline-none [&_input]:transition [&_input:focus]:border-slate-400 [&_input:focus]:shadow-none [&_input:focus]:ring-0";

export default function EditMapStep({
  googleMapsSearch,
  setGoogleMapsSearch,
  hadleGetMoreInformationsAboutLocal,
  googleID,
  setGoogleID,
  formErrors,
  logradouro,
  setLogradouro,
  cep,
  setCEP,
  number,
  setNumber,
  complement,
  setComplement,
  neighborhood,
  setNeighborhood,
  city,
  setCity,
  uf,
  setUF,
  floor,
  setFloor,
  latitude,
  longitude,
  setLoadingGoogleMapsApi,
  loadingRequest,
  handleGoToDescriptionForm,
  setFormOptionSelected,
  isDraft = false,
  isSavingDraft = false,
  handleClearAddressFields,
  handleSaveAddressDraft,
  handleDraftNextFromMap,
  propertyId,
  rentReasons = [],
  hasChildrenUnits,
  onChildrenCountChange,
}: EditMapStepProps) {
  const unitLockedFieldClass = hasChildrenUnits
    ? `${fieldClass} [&_input]:cursor-not-allowed [&_input]:bg-slate-100 [&_input]:text-slate-500`
    : fieldClass;

  return (
    <form
      className="w-full rounded-md border border-slate-200 bg-white p-5 sm:p-8 [&_.css-2b097c-container]:w-full [&_[class*='-control']]:min-h-[44px] [&_[class*='-control']]:rounded-md [&_[class*='-control']]:border [&_[class*='-control']]:border-solid [&_[class*='-control']]:border-slate-200 [&_[class*='-control']]:bg-white [&_[class*='-control']]:shadow-none [&>p]:mb-2 [&>p]:mt-2 [&>p]:text-sm [&>p]:font-medium [&>p]:text-slate-800"
    >
      <p>Endereço completo para busca</p>
      <GooglePlacesAutocomplete
        onLoadFailed={(err) => console.log(err)}
        autocompletionRequest={{
          componentRestrictions: { country: "br" },
          types: ["address"],
          location: {
            lat: -17.221666,
            lng: -46.875,
          },
          bounds: [
            {
              lat: -17.221666,
              lng: -46.875,
            },
            {
              lat: -17.221666,
              lng: -46.875,
            },
          ],
        }}
        selectProps={{
          styles: formSelectStyles,
          inputValue: googleMapsSearch,
          loadingMessage() {
            return "Carregando...";
          },
          onInputChange(e) {
            setGoogleMapsSearch(e);
          },
          onChange(e) {
            hadleGetMoreInformationsAboutLocal(
              e?.label,
              e?.value.place_id
            );
            setGoogleMapsSearch(e?.label);
            setGoogleID(!!e?.value.place_id ? e?.value.place_id : googleID);
          },
          noOptionsMessage() {
            return "Sem local";
          },
          placeholder: "",
        }}
      />

      <p className="mb-12 mt-3 text-xs font-normal text-slate-500 max-md:text-[10px]">
        Ex: Av. Dom Luís, 500 - Aldeota, Fortaleza - CE, Brasil
      </p>

      <div className="relative mt-8 w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-10 sm:px-6 md:px-10">
        <label className="absolute -top-3 left-1/2 w-[90%] -translate-x-1/2 rounded-md border border-slate-200 bg-white px-4 py-2 text-center text-xs font-medium text-slate-800 md:w-[55%] md:text-sm">
          Confira abaixo se os dados estão corretos:
        </label>

        <div className="mx-auto mt-2 flex max-w-full flex-col items-center justify-center gap-4 md:flex-row md:px-1">
          <div className={fieldClass}>
            <label>
              Logradouro *:{" "}
              {!!formErrors.logradouro && <p>{formErrors.logradouro}</p>}
            </label>
            <input
              type="text"
              value={logradouro}
              onChange={(e) => setLogradouro(e.target.value)}
            />
          </div>

          <div className={fieldClass}>
            <label>CEP * {!!formErrors.cep && <p>{formErrors.cep}</p>}</label>
            <MaskedInput
              mask={[/\d/, /\d/, /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/]}
              type="text"
              value={cep}
              onChange={(e) => setCEP(e.target.value)}
            />
          </div>

          <div className={fieldClass}>
            <label>
              Número * {!!formErrors.number && <p>{formErrors.number}</p>}
            </label>
            <input
              type="text"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
            />
          </div>

          <div className={unitLockedFieldClass}>
            <label>
              Complemento (opcional)
              {hasChildrenUnits ? (
                <span className="ml-1 text-[10px] font-normal text-slate-500">
                  — definido nas unidades filhas
                </span>
              ) : null}
            </label>
            <input
              type="text"
              value={complement}
              disabled={hasChildrenUnits}
              onChange={(e) => setComplement(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-4 flex max-w-full flex-col items-center justify-center gap-4 md:flex-row md:px-1">
          <div className={fieldClass}>
            <label>
              Bairro *{" "}
              {!!formErrors.neighborhood && <p>{formErrors.neighborhood}</p>}
            </label>
            <input
              type="text"
              value={neighborhood}
              onChange={(e) => setNeighborhood(e.target.value)}
            />
          </div>

          <div className={fieldClass}>
            <label>
              Cidade * {!!formErrors.city && <p>{formErrors.city}</p>}
            </label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>

          <div className={fieldClass}>
            <label>UF * {!!formErrors.uf && <p>{formErrors.uf}</p>}</label>
            <input
              type="text"
              value={uf}
              onChange={(e) => setUF(e.target.value)}
            />
          </div>

          <div className={unitLockedFieldClass}>
            <label>
              Andar {!!formErrors.floor && <p>{formErrors.floor}</p>}
              {hasChildrenUnits ? (
                <span className="ml-1 text-[10px] font-normal text-slate-500">
                  — definido nas unidades filhas
                </span>
              ) : null}
            </label>
            <input
              type="text"
              value={floor}
              disabled={hasChildrenUnits}
              onChange={(e) => setFloor(e.target.value)}
            />
          </div>
        </div>
      </div>

      <PropertyChildrenSection
        mode="manage"
        propertyId={propertyId}
        rentReasons={rentReasons}
        allowStatusActions={!isDraft}
        onChildrenCountChange={onChildrenCountChange}
      />

      <div
        style={{
          height: "300px",
          width: "100%",
          marginTop: "58px",
        }}
      >
        <GoogleMaps
          bootstrapURLKeys={{
            key: "AIzaSyDSD2QM4JfZsREcRj_8StrW3E587RayIMc",
          }}
          center={{
            lat: parseFloat(latitude.toString()),
            lng: parseFloat(longitude.toString()),
          }}
          defaultZoom={18}
          options={{
            mapTypeControl: true,
          }}
          layerTypes={["TrafficLayer", "TransitLayer"]}
          yesIWantToUseGoogleMapApiInternals={true}
          onGoogleApiLoaded={() => setLoadingGoogleMapsApi(false)}
        >
          <Marker lat={latitude} lng={longitude} color="#FF3838" />
        </GoogleMaps>
      </div>

      <ButtonsWrapper className="flex-wrap justify-center gap-2 sm:gap-3">
        {isDraft ? (
          <>
            <ResetButton
              type="button"
              onClick={handleClearAddressFields}
            >
              LIMPAR
            </ResetButton>
            <SaveDraftButton
              isLoading={isSavingDraft}
              onClick={handleSaveAddressDraft}
            >
              Salvar rascunho
            </SaveDraftButton>
            <NextButton
              type="button"
              onClick={() => {
                // #region agent log
                agentDebugLog({
                  hypothesisId: "V0",
                  location: "EditMapStep/NextButton",
                  message: "map next clicked",
                  data: {
                    isDraft: Boolean(isDraft),
                    hasHandler: typeof handleDraftNextFromMap === "function",
                  },
                });
                // #endregion
                if (isDraft && handleDraftNextFromMap) {
                  handleDraftNextFromMap();
                  return;
                }
                setFormOptionSelected("description");
                window.scrollTo(0, 0);
              }}
            >
              <p>Próximo</p>
              <ChevronRight className="h-[18px] w-[18px] text-white" />
            </NextButton>
          </>
        ) : (
          <>
            <RegisterButton
              type="button"
              onClick={!!loadingRequest ? () => {} : handleGoToDescriptionForm}
            >
              <p>{!!loadingRequest ? "Carregando..." : "Salvar"}</p>
            </RegisterButton>
            <NextButton
              type="button"
              onClick={() => {
                setFormOptionSelected("description");
                window.scrollTo(0, 0);
              }}
            >
              <p>Próximo</p>
              <ChevronRight className="h-[18px] w-[18px] text-white" />
            </NextButton>
          </>
        )}
      </ButtonsWrapper>
    </form>
  );
}
